/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_SUCCESS,
  Button,
  DialogContentProps,
  TextField,
} from '@squiz/sds';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFileStore } from '../../../../../../../../Services/file-store';
import { FILE_EXPLORER_STRINGS } from '../../../../FileExplorer.constants';
import { useFileExplorerStore } from '../../../../FileExplorer.state';

export const CreateFolderDialogContent = (props: DialogContentProps): JSX.Element => {
  const { t } = useTranslation();
  const { close, setButtonKeys, setButtons } = props;

  const [folderName, setFolderName] = useState('');
  const { getDirectoryContents, makeDirectory, loadingState } = useFileStore();
  const { fileExplorerPosition, setDirectoryContents } = useFileExplorerStore();

  const inputRef = useRef<HTMLInputElement>(null);

  const labels = useMemo(() => {
    return {
      cancel: t(FILE_EXPLORER_STRINGS.cancel),
      create: t(FILE_EXPLORER_STRINGS.create),
      folderName: t(FILE_EXPLORER_STRINGS.folderName),
    };
  }, [t]);

  const dispatchCreateFolder = useCallback(async (): Promise<void> => {
    // istanbul ignore next
    const folderName = inputRef.current?.value;
    if (!folderName) {
      return;
    }

    await makeDirectory({ source: fileExplorerPosition + folderName });
    setFolderName('');

    const fileList = await getDirectoryContents({ source: fileExplorerPosition });
    setDirectoryContents(fileList.data);

    close();
  }, [close, fileExplorerPosition, getDirectoryContents, makeDirectory, setDirectoryContents]);

  useEffect(() => {
    const cancelButton = (
      <Button
        classes="file-explorer__dialog-button-cancel-create-folder"
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => close()}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );

    const createButton = (
      <Button
        classes="file-explorer__dialog-button-create-folder"
        format={BUTTON_FORMAT_STRONG}
        isLoading={loadingState.makeDirectoryLoading}
        onClick={async (): Promise<void> => dispatchCreateFolder()}
        text={labels.create}
        theme={BUTTON_THEME_SUCCESS}
      />
    );

    setButtonKeys([`cancel`, `create`]);
    setButtons([cancelButton, createButton]);
  }, [close, dispatchCreateFolder, labels, loadingState, setButtonKeys, setButtons]);

  return (
    <div className="file-explorer__dialog-content create-folder">
      <TextField
        fieldLabel={labels.folderName}
        id={'create-folder-input'}
        label={labels.folderName}
        name={labels.folderName}
        ref={inputRef}
        value={folderName}
      />
    </div>
  );
};
